import { BrandedTooltip } from '@clickguard/design-system'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlag } from 'services/feature-flags'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { markAnnouncementSeen } from 'pages/workspace/asset/refunds/api'

const KEY = 'refunds_agent'

/**
 * §1 — Announcement coachmark on the Dashboard, anchored to the Refunds Agent
 * nav item (DS `BrandedTooltip`). Shown once per user per workspace. Spec B1.
 */
export const AnnouncementSpotlight = () => {
	const navigate = useNavigate()
	const flagOn = useFeatureFlag('refunds-agent-enabled')

	const account = useAccountStore((s) => s.account)
	const asset = useAssetStore((s) => s.asset)
	const { user, setUser } = useUserStore(
		useShallow((s) => ({ user: s.user, setUser: s.setUser }))
	)

	const anchorRef = useRef<HTMLElement | null>(null)
	const [anchorReady, setAnchorReady] = useState(false)
	const [dismissed, setDismissed] = useState(false)

	// The nav item lives in the sidebar; poll briefly for it after mount.
	useEffect(() => {
		let tries = 0
		let timer: ReturnType<typeof setTimeout>
		const find = () => {
			const el = document.querySelector(
				'[data-refunds-agent-nav]'
			) as HTMLElement | null
			if (el) {
				anchorRef.current = el
				setAnchorReady(true)
			} else if (tries++ < 10) {
				timer = setTimeout(find, 200)
			}
		}
		find()
		return () => clearTimeout(timer)
	}, [])

	const accountId = account?.id
	const subscription = account?.billing?.subscription
	const subscriptionActive =
		subscription?.status === 'ACTIVE' || subscription?.type === 'TRIAL'
	const setupComplete = account?.setup?.step === 'SETUP_COMPLETE'
	const alreadySeen =
		!!accountId && !!user?.seen_announcements?.[accountId]?.[KEY]

	const show =
		flagOn &&
		anchorReady &&
		!dismissed &&
		!alreadySeen &&
		subscriptionActive &&
		setupComplete &&
		!!accountId

	const markSeen = () => {
		setDismissed(true)
		if (accountId && user) {
			setUser({
				...user,
				seen_announcements: {
					...(user.seen_announcements ?? {}),
					[accountId]: {
						...(user.seen_announcements?.[accountId] ?? {}),
						[KEY]: new Date().toISOString()
					}
				}
			})
			markAnnouncementSeen(accountId).catch(() => undefined)
		}
	}

	const handleTryAI = () => {
		markSeen()
		if (accountId && asset?.id) {
			navigate(`/workspace/${accountId}/asset/${asset.id}/refunds`)
		}
	}

	if (!show) return null

	return (
		<BrandedTooltip
			open={show}
			anchorRef={anchorRef}
			placement="right"
			offset={28}
			width={429}
			compact={false}
			arrowOffset={200}
			mediaFullBleed
			media={
				<img
					src="/refunds-agent-announcement-bg.png"
					alt=""
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						objectPosition: 'center'
					}}
				/>
			}
			pillLabel="Refunds Agent"
			badge="New"
			title="Recover your wasted ad spend"
			closeLabel="Close"
			actionLabel="Try AI"
			onClose={markSeen}
			onAction={handleTryAI}
		>
			Our new Refund AI Agent turns the invalid clicks we&apos;ve flagged into
			Google Ads refund requests, files them for you, and works to get your
			wasted spend back.
		</BrandedTooltip>
	)
}
